import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Code2, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export function DashboardLayout({ title, menu, children, testid }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const Sidebar = (
    <div className="flex flex-col h-full">
      <Link to="/" className="flex items-center gap-2 px-5 h-16 border-b border-slate-800">
        <span className="grid place-items-center h-8 w-8 rounded-lg bg-indigo-600 text-white"><Code2 className="h-4 w-4" /></span>
        <span className="font-heading font-extrabold text-white">DS <span className="text-indigo-400">KODE</span></span>
      </Link>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 no-scrollbar">
        {menu.map((m) => {
          const active = location.pathname === m.to;
          return (
            <Link
              key={m.to}
              to={m.to}
              onClick={() => setOpen(false)}
              data-testid={`side-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? "bg-indigo-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <m.icon className="h-4.5 w-4.5 shrink-0" style={{ height: 18, width: 18 }} /> {m.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-800">
        <div className="flex items-center gap-2.5 px-2 py-2 mb-2">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-indigo-500/20 text-indigo-300 font-semibold text-sm overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U").toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{user?.name}</p>
            <p className="text-[11px] text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={doLogout} data-testid="dashboard-logout" className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium text-red-300 hover:bg-red-500/10 transition-colors">
          <LogOut className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} /> Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid={testid}>
      <aside className="hidden lg:flex w-64 bg-slate-900 fixed inset-y-0 left-0 z-40">{Sidebar}</aside>

      {open && (
        <>
          <div className="fixed inset-0 z-40 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />
          <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 lg:hidden animate-fade-up">{Sidebar}</aside>
        </>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 h-16 flex items-center px-4 sm:px-6 gap-3">
          <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-md hover:bg-slate-100" data-testid="dashboard-menu-toggle">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-heading font-bold text-lg text-slate-900">{title}</h1>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </div>
    </div>
  );
}
